import React, { useRef, useEffect, useState, useMemo } from "react";
import { Search, Trash2, Users } from "lucide-react";
import { BaseballTable } from "../../../components/tables/BaseballTable";

export function ListDetail(props) {
  const {
    selectedList,
    tableData,
    filter,
    onFilterChange,
    loadingPlayers,
    onRemovePlayer,
    search,
    onAddPlayer,
  } = props;

  const { searchTerm, setSearchTerm, results, loading: loadingSearch, loadAll } = search;

  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setShowSearch(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleAdd = (player) => {
    onAddPlayer(player);
    setSearchTerm("");
    setShowSearch(false);
  };

  const columns = useMemo(() => [
    {
      name: "Player",
      selector: (row) => row.player_name,
      sortable: true,
      minWidth: "150px",
      cell: (row) => <span className="font-medium text-sm truncate block">{row.player_name}</span>,
    },
    {
      name: "Team",
      selector: (row) => row.team_name,
      sortable: true,
      minWidth: "180px",
      cell: (row) => <span className="text-sm truncate block">{row.team_name || "N/A"}</span>,
    },
    {
      name: "Position",
      selector: (row) => row.position,
      sortable: true,
      width: "80px",
      cell: (row) => <span className="text-sm whitespace-nowrap">{row.position || "N/A"}</span>,
    },
    {
      name: "Years",
      selector: (row) => `${row.min_year ?? "N/A"} - ${row.max_year ?? "N/A"}`,
      sortable: true,
      width: "110px",
      cell: (row) => (
        <span className="text-sm whitespace-nowrap">
          {row.min_year ?? "N/A"} - {row.max_year ?? "N/A"}
        </span>
      ),
    },
    {
      name: "",
      width: "50px",
      cell: (row) => (
        <button
          onClick={() => onRemovePlayer(row.player_id)}
          className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
          title="Remove"
        >
          <Trash2 size={14} />
        </button>
      ),
    },
  ], [onRemovePlayer]);

  if (!selectedList) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm py-16 flex flex-col items-center justify-center">
        <div className="bg-blue-100 rounded-full p-3 mb-3">
          <Users className="text-blue-600" size={32} />
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">No list selected</h3>
        <p className="text-xs text-gray-500 text-center max-w-sm">
          Select a list from the sidebar or create a new one to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col h-[calc(100vh-240px)]">
      <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50 flex-shrink-0">
        <h2 className="text-lg font-semibold text-gray-900">{selectedList.name}</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          {tableData.length} {tableData.length === 1 ? "player" : "players"}
        </p>
      </div>

      <div className="px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filter}
              onChange={(e) => onFilterChange(e.target.value)}
              placeholder="Filter list..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white hover:border-gray-400"
            />
          </div>

          <div ref={searchRef} className="relative">
            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => { setShowSearch(true); loadAll(); }}
              placeholder="Add players..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white hover:border-gray-400"
            />
            {showSearch && searchTerm && (
              <div className="absolute mt-1 bg-white border border-gray-200 rounded-lg shadow-lg w-full max-h-64 overflow-y-auto z-50">
                {loadingSearch ? (
                  <div className="p-3 text-xs text-gray-500 text-center">
                    <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2" />
                    Loading...
                  </div>
                ) : results.length === 0 ? (
                  <div className="p-3 text-xs text-gray-500 text-center">No matches</div>
                ) : (
                  <div className="py-1">
                    {results.map((p) => (
                      <button
                        key={p.player_id}
                        onClick={() => handleAdd(p)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 flex justify-between items-center transition-colors border-b border-gray-100 last:border-b-0 text-sm"
                      >
                        <span className="font-medium text-gray-900 truncate">{p.player_name}</span>
                        <span className="text-xs text-gray-500 ml-2 truncate">{p.team_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto min-h-0">
        {loadingPlayers ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2" />
            <p className="text-xs text-gray-500">Loading...</p>
          </div>
        ) : tableData.length === 0 ? (
          <div className="p-12 text-center">
            {filter ? (
              <>
                <Search className="mx-auto text-gray-300 mb-2" size={24} />
                <p className="text-xs font-medium text-gray-900 mb-1">No matches</p>
                <p className="text-xs text-gray-500">Adjust your filter</p>
              </>
            ) : (
              <>
                <Users className="mx-auto text-gray-300 mb-2" size={24} />
                <p className="text-xs font-medium text-gray-900 mb-1">No players</p>
                <p className="text-xs text-gray-500">Add players above</p>
              </>
            )}
          </div>
        ) : (
          <div className="p-4">
            <BaseballTable
              data={tableData}
              columns={columns}
              filename={`${selectedList.name || "player_list"}.csv`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
